import { useState } from 'react'
import { Copy, Check, AlertCircle } from 'lucide-react'
import { buildOpenBugsMarkdown } from '../lib/copyBugReports'
import { useLoadAllProjectBugs } from '../hooks/useLoadAllProjectBugs'
import './CopyBugReportsButton.css'

interface CopyBugReportsButtonProps {
  projectId: string
  status?: string
  severitySort?: 'asc' | 'desc' | null
  severity?: string
  kind?: string
  environment?: string
  disabled?: boolean
  className?: string
}

// Green, prominent "Copy Bug Reports" call-to-action. Shared by the bug list tab and the
// overview banner so both copy identical open-bug markdown and stay visually consistent.
export default function CopyBugReportsButton({ projectId, status, severitySort, severity, kind, environment, disabled, className }: CopyBugReportsButtonProps) {
  // 'failed' matters: the fetch or the clipboard write can both throw (clipboard permission is the
  // common one), and silently dropping back to 'idle' left the user unable to tell a failed copy from
  // a successful one.
  const [copyState, setCopyState] = useState<'idle' | 'loading' | 'copied' | 'failed'>('idle')
  const loadAllBugs = useLoadAllProjectBugs()

  async function handleCopy() {
    setCopyState('loading')
    try {
      const allBugs = await loadAllBugs({ projectId, status, severitySort, severity, kind, environment })
      const markdown = buildOpenBugsMarkdown(allBugs)
      await navigator.clipboard.writeText(markdown)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      setCopyState('failed')
      setTimeout(() => setCopyState('idle'), 4000)
    }
  }

  const label = copyState === 'loading'
    ? 'Copying...'
    : copyState === 'copied'
      ? 'Copied'
      : copyState === 'failed'
        ? "Couldn't copy — try again"
        : 'Copy Bug Reports'

  return (
    <button
      type="button"
      className={`copy-bug-reports-btn${copyState === 'failed' ? ' copy-bug-reports-btn--failed' : ''}${className ? ` ${className}` : ''}`}
      onClick={handleCopy}
      disabled={disabled || copyState === 'loading'}
      data-testid="copy-all-markdown-btn"
      data-analytics-event="bug-copy-reports"
      aria-label={label}
    >
      {copyState === 'copied'
        ? <Check size={14} aria-hidden="true" />
        : copyState === 'failed'
          ? <AlertCircle size={14} aria-hidden="true" />
          : <Copy size={14} aria-hidden="true" />}
      {label}
      {/* Announce a failure to assistive tech — the label change alone isn't in a live region. */}
      <span role="status" aria-live="polite" className="sr-only">
        {copyState === 'failed' ? 'Copying bug reports failed' : ''}
      </span>
    </button>
  )
}
